/**
 * Customer SPA helpers for Chats soft/hard + why-chip (Landau WP-FE-A1 / PM-ESC-3).
 *
 * Operator EN meaning must align with server `ESCALATION_KIND_LABEL` /
 * `ESCALATION_FAMILY_LABEL` — copy lives in `src/locales` (do not import server).
 * Unknown family slugs → omit (deny-by-default); never show raw triggers.
 */

import type { TFunction } from "i18next";

export type EscalationKindUi = "soft" | "hard";

/** Closed family slugs (migration 067 / escalation-reason-vocabulary). */
export const ESCALATION_REASON_FAMILY_SLUGS = [
  "human_requested",
  "tool_or_platform_failure",
  "availability_unverified",
  "no_availability",
  "account_read_failed",
  "conversation_stall",
  "low_confidence_or_scope",
  "frustration",
  "max_exchanges",
  "identity_verification",
  "policy_restricted",
  "manual",
  "ai_unavailable",
  // BE-220 PM-BE220-4 (d) / PM-BE220-7 — cancel-all named set left unfinished. Server emits once Ship B lands.
  "named_set_unfinished",
  // PM-HR2-1 (b) — cancellation-authority denial path. Server emits once Phase 10 wires it.
  "cancellation_refused",
] as const;

export type EscalationReasonFamilyUi = (typeof ESCALATION_REASON_FAMILY_SLUGS)[number];

const FAMILY_SET: ReadonlySet<string> = new Set(ESCALATION_REASON_FAMILY_SLUGS);

export function parseEscalationKind(value: unknown): EscalationKindUi | undefined {
  if (value === "soft" || value === "hard") return value;
  return undefined;
}

export function parseEscalationReasonFamily(value: unknown): EscalationReasonFamilyUi | undefined {
  if (typeof value !== "string") return undefined;
  const key = value.trim();
  if (!key || !FAMILY_SET.has(key)) return undefined;
  return key as EscalationReasonFamilyUi;
}

export function escalationKindLabel(kind: EscalationKindUi | undefined, t: TFunction): string | null {
  if (kind === "soft") return String(t("chats.escalationKind.soft"));
  if (kind === "hard") return String(t("chats.escalationKind.hard"));
  return null;
}

/** Known family → i18n label; unknown/null → null (omit; never raw slug). */
export function escalationFamilyLabel(
  family: string | null | undefined,
  t: TFunction,
): string | null {
  const parsed = parseEscalationReasonFamily(family);
  if (!parsed) return null;
  return String(t(`chats.escalationFamily.${parsed}`));
}

/** "Needs review · Customer asked for a human" — omit empty parts. */
export function formatEscalationWhyChip(
  kind: EscalationKindUi | undefined,
  family: string | null | undefined,
  t: TFunction,
): string | null {
  const parts = [escalationKindLabel(kind, t), escalationFamilyLabel(family, t)].filter(
    (p): p is string => Boolean(p && p.trim()),
  );
  return parts.length > 0 ? parts.join(" · ") : null;
}
